
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface SiteSettings {
  id?: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  site_title: string;
  site_description: string;
  social_facebook?: string;
  social_instagram?: string;
  social_youtube?: string;
  social_tiktok?: string;
  privacy_policy_content?: string;
  terms_of_service_content?: string;
  privacy_effective_date?: string;
  terms_effective_date?: string;
  copyright_start_year?: number;
  legal_email?: string;
  legal_phone?: string;
  legal_address?: string;
}

const QUERY_KEY = ['site-settings'];

export const useSiteSettings = () => {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      console.log("Fetching site settings...");
      
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("Error fetching site settings:", error);
        throw error;
      }

      console.log("Site settings loaded:", data);
      return data as SiteSettings | null;
    },
    staleTime: 1000 * 30, // Consider data fresh for only 30 seconds to ensure updates are visible
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false, // Disable auto-refetch on focus to prevent interference
    refetchOnMount: false, // Don't auto-refetch on mount to use cached data when available
  });
};

export const useSiteSettingsMutation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (settings: SiteSettings) => {
      console.log("Saving settings via mutation:", settings);
      
      // Get current user to verify authentication
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      console.log("Current user:", user?.id, "Auth error:", authError);
      
      if (!user) {
        throw new Error("User not authenticated");
      }
      
      // Check if user is admin
      const { data: isAdminResult, error: adminError } = await supabase.rpc('is_admin');
      console.log("Is admin check result:", isAdminResult, "Admin error:", adminError);
      
      if (adminError) {
        console.error("Error checking admin status:", adminError);
        throw adminError;
      }
      
      if (!isAdminResult) {
        throw new Error("User is not authorized to modify site settings");
      }
      
      // Check if a settings record exists
      const { data: existingData, error: fetchError } = await supabase
        .from('site_settings')
        .select('id')
        .limit(1)
        .maybeSingle();

      if (fetchError) {
        console.error("Error checking existing settings:", fetchError);
        throw fetchError;
      }

      let result;
      
      if (existingData) {
        console.log("Updating existing record with ID:", existingData.id);
        // Update existing record
        result = await supabase
          .from('site_settings')
          .update(settings)
          .eq('id', existingData.id)
          .select()
          .single();
      } else {
        console.log("Creating new record");
        // Insert new record
        result = await supabase
          .from('site_settings')
          .insert([settings])
          .select()
          .single();
      }

      if (result.error) {
        console.error("Error saving settings:", result.error);
        console.error("Error details:", {
          code: result.error.code,
          message: result.error.message,
          details: result.error.details,
          hint: result.error.hint
        });
        throw result.error;
      }

      console.log("Settings saved successfully:", result.data);
      return result.data;
    },
    onSuccess: (data) => {
      console.log("Mutation successful, forcing cache refresh with:", data);
      
      // Immediately set the new data in cache
      queryClient.setQueryData(QUERY_KEY, data);
      
      // Invalidate all queries to force fresh fetches everywhere
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      
      // Force an immediate refetch to ensure all components get updated
      queryClient.refetchQueries({ 
        queryKey: QUERY_KEY,
        type: 'active' // Only refetch active queries
      });
      
      toast({
        title: "Success",
        description: "Site settings saved successfully",
      });
    },
    onError: (error: any) => {
      console.error("Mutation error:", error);
      
      let errorMessage = "Failed to save site settings";
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.details) {
        errorMessage = error.details;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });
};
